import {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  ReactNode,
  useMemo,
} from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { getSiteUrlForPath } from "@/lib/site-url";
import { clearReturnPath, takeReturnPath } from "@/lib/auth-return-path";
import {
  isObfuscatedDuplicateSignup,
  mapSignupError,
} from "@/lib/signup-error";
import {
  isSessionMissingAuthError,
  isTimeoutLikeAuthError,
} from "@/lib/auth-errors";
import {
  setErrorTrackingTags,
  setErrorTrackingUser,
} from "@/lib/error-tracking";
import posthog from "posthog-js";
import type { AppRole } from "@/lib/roles";
import {
  canActAsUser,
  canManageTenant,
  hasOperatorRole,
  hasStaffRole,
  isOperatorPortal,
  isOwnerOnly,
  canUseDemoDualView,
  parseUserRoles,
} from "@/lib/roles";

/** Tenant or owner an admin is currently "acting as". */
interface ImpersonatedUser {
  id: string;
  name: string | null;
  email: string;
  /** Which portal we dropped into — drives the banner label + exit copy. */
  kind?: "tenant" | "owner";
}

// Persist across reloads so the "acting as" banner + exit survive a refresh.
// The active (tenant) session already lives in localStorage; we additionally
// stash the admin's own session so "Exit" can restore it.
const IMPERSONATION_TARGET_KEY = "coliviq.impersonation.target";
const IMPERSONATION_ADMIN_SESSION_KEY = "coliviq.impersonation.adminSession";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  /** Staff (super_admin, tech, admin) or owner — can open /admin console. */
  isAdmin: boolean;
  roles: AppRole[];
  isSuperAdmin: boolean;
  isTech: boolean;
  hasSalesAdminRole: boolean;
  hasStaffRole: boolean;
  hasOwnerRole: boolean;
  /** Owner without staff roles — limited to the owner portal tab. */
  isOwnerPortalOnly: boolean;
  /**
   * COL-447. True only for the flagged sales-demo account, which is both the
   * owner of the hidden demo property and a tenant in one of its rooms. It lets
   * the client offer that one account a route to the tenant dashboard, which
   * `isOwnerPortalOnly` otherwise closes off entirely.
   *
   * Presentation-only: this never widens what the account may READ. It holds
   * the real `owner` role and real booking rows, and RLS governs every query it
   * makes exactly as before — this only decides which screens we render.
   */
  isDemoDualViewAccount: boolean;
  /** True once the demo flag has been resolved for the current user. */
  isDemoDualViewReady: boolean;
  hasOperatorRole: boolean;
  /** Operator without staff roles — sees the operator-scoped console. */
  isOperatorPortal: boolean;
  /** Super-admin or tech — integrations, audit-heavy tools. */
  hasTechOrSuperAdmin: boolean;
  /**
   * Admin or super-admin — may edit tenant records, date of birth, handover
   * protocols and booking status. Excludes tech, and mirrors the SQL
   * `has_role(uid, 'admin')` predicate the booking-status trigger enforces.
   */
  canManageTenant: boolean;
  /**
   * Admin or super-admin — may "act as" (impersonate) a tenant or owner.
   * Mirrors the edge-function gate; the backend additionally refuses an
   * `admin` acting as any staff account.
   */
  canActAs: boolean;
  loading: boolean;
  /** True once roles have been resolved for the signed-in user (COL-255). */
  rolesReady: boolean;
  signUp: (
    email: string,
    password: string,
    name: string,
    options?: {
      /** Where the confirmation email lands; defaults to "/". */
      redirectPath?: string;
      /** Extra raw_user_meta_data (e.g. operator signup provisioning keys). */
      metadata?: Record<string, string>;
    }
  ) => Promise<{ error: any }>;

  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;

  resetPasswordForEmail: (email: string) => Promise<{ error: any }>;

  resetPassword: (newPassword: string) => Promise<{ error: any }>;
  refreshSession: () => Promise<void>;
  refreshRoles: () => Promise<void>;

  /** True while an admin is viewing the app as a tenant or owner. */
  isImpersonating: boolean;
  /** The tenant being impersonated, or null. */
  impersonatedUser: ImpersonatedUser | null;
  /** Admin / super-admin: swap the session to the given tenant and open their dashboard. */
  actAsTenant: (targetUserId: string) => Promise<{ error: any }>;
  /** Admin / super-admin: swap the session to the given owner and open the owner portal. */
  actAsOwner: (targetUserId: string) => Promise<{ error: any }>;
  /** Restore the admin's own session and return to the admin console. */
  stopImpersonating: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [loading, setLoading] = useState(true);
  // True once roles have been resolved for the current user. Roles load
  // asynchronously AFTER the session is known, so a hard load of a deep
  // /admin route briefly has user set but roles still empty — without this
  // guard, requireAdmin routes bounced to /dashboard before roles arrived
  // (COL-255: "open in new tab → dashboard").
  const [rolesReady, setRolesReady] = useState(false);
  /**
   * `profiles.is_demo_account`, KEYED to the account it was resolved for
   * (COL-447). Keying rather than a bare boolean is load-bearing in two
   * directions:
   *  - a value resolved for a previous account can never apply to the next one,
   *    even though `roles` and this state commit on different ticks; and
   *  - an ordinary refresh of the SAME account (refreshRoles, TOKEN_REFRESHED)
   *    keeps the resolved value instead of blinking it off, which would bounce
   *    a demo owner off /dashboard mid-demo.
   * `null` means "not resolved for anyone yet".
   */
  const [demoFlag, setDemoFlag] = useState<{
    userId: string;
    value: boolean;
  } | null>(null);
  const [impersonatedUser, setImpersonatedUser] =
    useState<ImpersonatedUser | null>(() => {
      try {
        const raw = localStorage.getItem(IMPERSONATION_TARGET_KEY);
        return raw ? (JSON.parse(raw) as ImpersonatedUser) : null;
      } catch {
        return null;
      }
    });
  const navigate = useNavigate();

  // Guards against a double-click / double-render minting a second magic-link
  // token: each generateLink call rotates the user's token, so a second mint
  // invalidates the first one verifyOtp is about to consume (→ otp_expired).
  const actAsInFlightRef = useRef(false);

  /**
   * Monotonic token for role/demo-flag loads (COL-447). Two loads can be in
   * flight at once — switching accounts fires a second before the first has
   * finished — and the profile lookup of the OLDER one can land last. Without
   * this, that stale response would overwrite the newer account's resolved flag
   * with a record keyed to the previous user, leaving readiness stuck false and
   * suppressing the Dashboard redirect for a normal owner.
   */
  const rolesLoadSeqRef = useRef(0);

  /**
   * Upper bound on the demo-flag lookup. `supabase.ts` applies its request
   * timeout to auth endpoints only, so this query could otherwise hang forever
   * — and because the Dashboard redirect waits for readiness, a hang would
   * silently strand a normal owner on the tenant dashboard. Timing out resolves
   * readiness with the non-demo answer, which is the pre-existing behaviour.
   */
  const DEMO_FLAG_TIMEOUT_MS = 5000;

  const isSuperAdmin = roles.includes("super_admin");
  const isTech = roles.includes("tech");
  const hasSalesAdminRole = roles.includes("admin");
  const hasStaffRoleFlag = useMemo(() => hasStaffRole(roles), [roles]);
  const hasOwnerRole = roles.includes("owner");
  const hasOperatorRoleFlag = roles.includes("operator");
  const hasConsoleAccess =
    hasStaffRoleFlag || hasOwnerRole || hasOperatorRoleFlag;
  const isOwnerPortalOnly = useMemo(() => isOwnerOnly(roles), [roles]);
  // Owner-portal-only AND flagged as the demo account. Gated on the role too,
  // so flipping the column on a staff or tenant account changes nothing.
  const isDemoAccount =
    demoFlag !== null && user?.id === demoFlag.userId && demoFlag.value;
  /**
   * The flag has been resolved for the CURRENT user. Redirect decisions must
   * wait for this — on a cold load of /dashboard the roles arrive first, and
   * acting on them alone would bounce the demo account away before its flag
   * had a chance to land.
   */
  const isDemoDualViewReady = demoFlag !== null && user?.id === demoFlag.userId;
  const isDemoDualViewAccount = useMemo(
    () => canUseDemoDualView(roles, isDemoAccount),
    [roles, isDemoAccount]
  );
  const isOperatorPortalFlag = useMemo(() => isOperatorPortal(roles), [roles]);
  const hasTechOrSuperAdmin = isSuperAdmin || isTech;
  const canManageTenantFlag = useMemo(() => canManageTenant(roles), [roles]);
  const canActAs = useMemo(() => canActAsUser(roles), [roles]);

  const loadRolesForUser = async (userId: string) => {
    const loadSeq = ++rolesLoadSeqRef.current;
    /** Drop the result if a newer load has started since this one began. */
    const applyDemoFlag = (value: boolean) => {
      if (rolesLoadSeqRef.current !== loadSeq) return;
      setDemoFlag({ userId, value });
    };
    try {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId);

      if (error) {
        console.error("Error loading roles:", error);
        setRoles([]);
        applyDemoFlag(false);
        setErrorTrackingTags({ is_admin: false });
        return;
      }

      const next = parseUserRoles(data as { role: AppRole }[]);
      setRoles(next);
      const consoleAccess = hasStaffRole(next) || next.includes("owner");
      setErrorTrackingTags({ is_admin: consoleAccess });

      // Demo flag (COL-447). Only owner accounts can act on it, so skip the
      // round trip for everyone else. Any failure — a timeout, or an
      // environment where the column has not been migrated yet — degrades to
      // `false`, which is the pre-existing behaviour.
      if (next.includes("owner")) {
        try {
          const profileResult = await Promise.race([
            supabase
              .from("profiles")
              .select("is_demo_account")
              .eq("id", userId)
              .maybeSingle(),
            new Promise<null>((resolve) =>
              setTimeout(() => resolve(null), DEMO_FLAG_TIMEOUT_MS)
            ),
          ]);
          applyDemoFlag(
            profileResult !== null &&
              !profileResult.error &&
              (profileResult.data as { is_demo_account?: boolean } | null)
                ?.is_demo_account === true
          );
        } catch {
          applyDemoFlag(false);
        }
      } else {
        applyDemoFlag(false);
      }
    } catch (e) {
      console.error("Error loading roles:", e);
      setRoles([]);
      applyDemoFlag(false);
      setErrorTrackingTags({ is_admin: false });
    } finally {
      // Roles resolved (success or failure) — release the requireAdmin gate.
      setRolesReady(true);
    }
  };

  useEffect(() => {
    let isMounted = true;

    // Set up auth state listener FIRST
    // Supabase automatically handles token refresh with autoRefreshToken: true
    // We only need to listen to state changes and handle errors
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!isMounted) return;

      setSession(session);
      setUser(session?.user ?? null);
      setErrorTrackingUser(session?.user ? { id: session.user.id } : null);

      // Handle different auth events
      switch (event) {
        case "INITIAL_SESSION":
        case "SIGNED_IN":
        case "TOKEN_REFRESHED":
        case "USER_UPDATED":
          if (session?.user) {
            setErrorTrackingTags({ auth_event: event });
            posthog.identify(session.user.id, {
              email: session.user.email,
              // owner | operator | tenant — set as signup metadata (Operators /
              // OwnerSignup); the generic AuthModal path has none, so it's a
              // tenant. Trait travels with the person on every sign-in/refresh,
              // so owner-vs-tenant segmentation holds across sessions.
              account_type:
                session.user.user_metadata?.account_type ?? "tenant",
            });
            // Use setTimeout to avoid blocking the main thread
            setTimeout(() => {
              if (isMounted) {
                loadRolesForUser(session.user.id);
              }
            }, 0);
          }
          break;
        case "SIGNED_OUT":
          setSession(null);
          setUser(null);
          setRoles([]);
          setDemoFlag(null);
          setRolesReady(false);
          setErrorTrackingUser(null);
          posthog.reset();
          break;
        case "PASSWORD_RECOVERY":
          // User clicked password reset link - handled in ResetPassword component
          break;
      }

      setLoading(false);
    });

    // Session refresh is left entirely to supabase-js (autoRefreshToken +
    // cross-tab Web Lock). A manual 30s watchdog used to live here; it raced
    // the library's own rotation and escalated any single-tab invalid_grant
    // into a GLOBAL signOut that logged the user out of every tab and device.
    // Removed — see COL-536-adjacent auth fix.

    // Initial session. (Dev auto-login was removed — it read the service-role
    // key from a VITE_ variable in the browser to mint a session, which
    // risks the service-role key inlining into a bundle. Sign in manually in
    // dev; re-add via a dev-only EDGE FUNCTION if the convenience is wanted.
    // ponytail: deleted the footgun rather than guard it; edge fn is the path.)
    void supabase.auth.getSession().then(({ data: { session } }) => {
      if (!isMounted) return;

      const finalSession = session;
      setSession(finalSession);
      setUser(finalSession?.user ?? null);
      setErrorTrackingUser(
        finalSession?.user ? { id: finalSession.user.id } : null
      );

      if (finalSession?.user) {
        loadRolesForUser(finalSession.user.id);
      }
      setLoading(false);
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const refreshRoles = async () => {
    if (user?.id) {
      await loadRolesForUser(user.id);
    }
  };

  const isImpersonating = impersonatedUser !== null;

  // Swap the browser session to a tenant via a backend-minted magic-link token.
  // auth.uid() becomes the tenant, so the existing tenant dashboard + RLS work
  // unchanged. The admin's own session is stashed first so Exit can restore it.
  const impersonate = async (
    targetUserId: string,
    kind: "tenant" | "owner" = "tenant"
  ) => {
    if (!canActAs) {
      const err = new Error("Only an admin can act as another account.");
      toast.error(err.message);
      return { error: err };
    }
    // Reject re-entry while a swap is already running. Without this, a second
    // generateLink call would invalidate the first token (→ otp_expired).
    if (actAsInFlightRef.current) {
      return { error: null };
    }
    actAsInFlightRef.current = true;
    try {
      const {
        data: { session: adminSession },
      } = await supabase.auth.getSession();
      if (!adminSession) {
        const err = new Error("No active session.");
        toast.error(err.message);
        return { error: err };
      }

      const { data, error } = await supabase.functions.invoke(
        "admin-act-as-tenant",
        { body: { target_user_id: targetUserId } }
      );

      if (error || !data?.ok || !data?.hashed_token || !data?.email) {
        // On a non-2xx response, functions.invoke returns a generic
        // FunctionsHttpError ("…non-2xx status code") and stashes the real
        // JSON body ({ error }) on error.context. Surface that instead.
        let message = "Failed to start impersonation.";
        const ctx = (error as any)?.context;
        if (ctx && typeof ctx.json === "function") {
          try {
            const body = await ctx.json();
            if (body?.error) message = body.error as string;
          } catch {
            /* fall through to generic message */
          }
        } else if (data && (data.error as string)) {
          message = data.error as string;
        } else if (error?.message) {
          message = error.message;
        }
        toast.error(message);
        return { error: error ?? new Error(message) };
      }

      // Stash the admin session BEFORE the swap clears it from localStorage.
      localStorage.setItem(
        IMPERSONATION_ADMIN_SESSION_KEY,
        JSON.stringify({
          access_token: adminSession.access_token,
          refresh_token: adminSession.refresh_token,
        })
      );

      // hashed_token from generateLink is a token_hash (already hashed), so it
      // must be verified via `token_hash` — NOT `{ email, token }`, which treats
      // the value as a plaintext OTP, hashes it again, and fails the lookup with
      // a misleading "otp_expired / Token has expired or is invalid".
      const { error: otpError } = await supabase.auth.verifyOtp({
        token_hash: data.hashed_token as string,
        type: "magiclink",
      });

      if (otpError) {
        localStorage.removeItem(IMPERSONATION_ADMIN_SESSION_KEY);
        toast.error(otpError.message);
        return { error: otpError };
      }

      const target: ImpersonatedUser = {
        ...(data.target as ImpersonatedUser),
        kind,
      };
      localStorage.setItem(IMPERSONATION_TARGET_KEY, JSON.stringify(target));
      setImpersonatedUser(target);
      // Event name kept as-is so the existing PostHog dashboards keep
      // reporting; actor_role distinguishes the newly allowed admin callers.
      posthog.capture(`super_admin acted as ${kind}`, {
        target_id: target.id,
        actor_role: isSuperAdmin ? "super_admin" : "admin",
      });
      toast.success(`Now acting as ${target.name || target.email}`);
      // Owners live in the owner portal (analytics tab), tenants on the tenant
      // dashboard — drop into the right landing surface (COL-254).
      navigate(kind === "owner" ? "/admin?tab=analytics" : "/dashboard");
      return { error: null };
    } catch (e: any) {
      localStorage.removeItem(IMPERSONATION_ADMIN_SESSION_KEY);
      console.error("impersonate failed:", e);
      toast.error(e?.message || "Failed to start impersonation.");
      return { error: e };
    } finally {
      actAsInFlightRef.current = false;
    }
  };

  const actAsTenant = (targetUserId: string) =>
    impersonate(targetUserId, "tenant");
  const actAsOwner = (targetUserId: string) =>
    impersonate(targetUserId, "owner");

  const stopImpersonating = async () => {
    const raw = localStorage.getItem(IMPERSONATION_ADMIN_SESSION_KEY);
    localStorage.removeItem(IMPERSONATION_TARGET_KEY);
    localStorage.removeItem(IMPERSONATION_ADMIN_SESSION_KEY);
    setImpersonatedUser(null);

    if (!raw) {
      // Nothing to restore — fall back to a clean sign-out.
      await supabase.auth.signOut();
      navigate("/", { replace: true });
      return;
    }

    try {
      const saved = JSON.parse(raw) as {
        access_token: string;
        refresh_token: string;
      };
      const { error } = await supabase.auth.setSession({
        access_token: saved.access_token,
        refresh_token: saved.refresh_token,
      });
      if (error) {
        toast.error(
          "Could not restore your admin session. Please sign in again."
        );
        await supabase.auth.signOut();
        navigate("/", { replace: true });
        return;
      }
      toast.success("Returned to your admin account.");
      navigate("/admin?tab=tenants");
    } catch (e) {
      console.error("stopImpersonating failed:", e);
      await supabase.auth.signOut();
      navigate("/", { replace: true });
    }
  };

  const signUp = async (
    email: string,
    password: string,
    name: string,
    options?: { redirectPath?: string; metadata?: Record<string, string> }
  ) => {
    try {
      const redirectUrl = getSiteUrlForPath(options?.redirectPath ?? "/");

      console.log("Signing up user:", { email, name });

      // Pre-check: is this email already registered? email_exists is SECURITY
      // DEFINER and answers only true/false. Anonymous visitors cannot read
      // profiles (COL-545), so there is no direct-query fallback; if the RPC
      // fails, GoTrue's own duplicate handling below still applies.
      try {
        const { data: emailExists, error: functionError } = await supabase.rpc(
          "email_exists",
          { check_email: email.toLowerCase().trim() }
        );

        if (!functionError && emailExists === true) {
          console.warn(
            "User already exists (via email_exists function):",
            email
          );
          const errorMessage =
            "An account with this email already exists. Please sign in instead.";
          toast.error(errorMessage);
          return { error: { message: errorMessage, isDuplicateUser: true } };
        }
      } catch (preCheckError) {
        // If pre-check fails, continue with signup attempt
        // Supabase will handle the duplicate check
        console.warn(
          "Pre-check for duplicate user failed, continuing with signup:",
          preCheckError
        );
      }

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            name: name,
            // Confirm-signup email renders in this language (DE if the user was
            // on the German site/form, else EN). Read the persisted UI language
            // — LanguageProvider wraps AuthProvider, so we can't useLanguage here.
            language:
              (typeof window !== "undefined" &&
                localStorage.getItem("language")) === "de"
                ? "de"
                : "en",
            ...(options?.metadata ?? {}),
          },
        },
      });

      if (error) {
        console.error("Signup error:", error);

        // Classify the error (duplicate / password / rate-limit / invalid
        // email / unknown) via a tested pure helper — a rate-limit error
        // ("email rate limit exceeded") must not read as "invalid email".
        const { message: userFriendlyMessage, isDuplicateUser } =
          mapSignupError(error);

        toast.error(userFriendlyMessage);
        return {
          error: { ...error, message: userFriendlyMessage, isDuplicateUser },
        };
      }

      // Post-check: with email confirmation on, GoTrue answers a duplicate
      // signup with success and an obfuscated user whose `identities` is empty.
      // That is its documented duplicate signal — reading profiles by email
      // here would need the anonymous access COL-545 removed.
      if (isObfuscatedDuplicateSignup(data?.user)) {
        console.warn("Duplicate user detected: signUp returned no identities", {
          email,
        });
        const errorMessage =
          "An account with this email already exists. Please sign in instead.";
        toast.error(errorMessage);
        return { error: { message: errorMessage, isDuplicateUser: true } };
      }

      // Check if user was created
      if (!data.user) {
        console.error("Signup failed: No user returned", data);
        toast.error("Failed to create account. Please try again.");
        return { error: { message: "User creation failed" } };
      }

      console.log("User created:", {
        userId: data.user.id,
        email: data.user.email,
        hasSession: !!data.session,
      });

      // Check if profile was created (wait a bit for trigger to execute).
      // Only with a session: before email confirmation the caller is anonymous
      // and cannot read profiles (COL-545), so the check would always "fail".
      if (data.session) {
        setTimeout(async () => {
          try {
            const { data: profile, error: profileError } = await supabase
              .from("profiles")
              .select("*")
              .eq("id", data.user.id)
              .single();

            if (profileError) {
              console.error("⚠️ Profile not created by trigger:", profileError);
              console.error("⚠️ This might indicate a database trigger issue");
              console.log(
                "💡 To fix this user manually, run this SQL in Supabase SQL Editor:"
              );
              console.log(`
INSERT INTO public.profiles (id, email, name)
SELECT id, email, COALESCE(raw_user_meta_data->>'name', '')
FROM auth.users
WHERE id = '${data.user.id}'
ON CONFLICT (id) DO UPDATE
SET email = EXCLUDED.email, name = EXCLUDED.name;

INSERT INTO public.user_roles (user_id, role)
VALUES ('${data.user.id}', 'user')
ON CONFLICT (user_id, role) DO NOTHING;
            `);
              console.log(
                "💡 Check Supabase Dashboard > Logs > Postgres Logs for trigger errors"
              );
            } else {
              console.log("✅ Profile created successfully:", profile);
            }
          } catch (err) {
            console.error("Error checking profile:", err);
          }
        }, 2000); // Increased wait time to 2 seconds
      }

      // Check if email confirmation is required
      if (!data.session) {
        // Email confirmation is required
        console.log("Email confirmation required for:", email);
        console.log("💡 If you don't receive an email, check:");
        console.log(
          "   1. Supabase Dashboard > Authentication > Email Templates"
        );
        console.log("   2. Supabase Dashboard > Settings > Auth > Email Auth");
        console.log("   3. Check spam folder");
        console.log("   4. In development, you can disable email confirmation");
        posthog.capture("user signed up", {
          email: data.user.email,
          method: "email",
          requires_confirmation: true,
          account_type: options?.metadata?.account_type ?? "tenant",
        });
        toast.success(
          "Account created! Please check your email to confirm your account before signing in."
        );
        return { error: null };
      }

      // Session was created (email confirmation disabled or auto-confirmed)
      console.log("User signed up and logged in automatically");
      toast.success("Account created successfully! You are now signed in.");

      posthog.capture("user signed up", {
        email: data.user.email,
        method: "email",
        account_type: options?.metadata?.account_type ?? "tenant",
      });

      // The auth state listener will handle setting the user and session
      // Navigate to dashboard after a short delay to allow state to update
      setTimeout(() => {
        navigate("/dashboard");
      }, 500);

      return { error: null };
    } catch (err: any) {
      console.error("Unexpected signup error:", err);
      toast.error(
        err.message || "An unexpected error occurred. Please try again."
      );
      return { error: err };
    }
  };

  const signIn = async (email: string, password: string) => {
    const { data: signData, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      toast.error(error.message);
      return { error };
    }

    posthog.capture("user signed in", { method: "email" });
    toast.success("Logged in successfully!");

    const uid = signData.user?.id;
    if (uid) {
      const { data: roleRows } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", uid);
      const rlist = parseUserRoles(roleRows as { role: AppRole }[] | null);
      // This path sets roles itself instead of going through
      // `loadRolesForUser`, and it can commit them while `user` is still the
      // PREVIOUS account (signing in from the AuthModal in a tab that is still
      // authenticated). The user-keying on `demoFlag` cannot help there — the
      // stale key still matches the stale user — so a flag belonging to anyone
      // other than the account signing in has to go.
      //
      // Keyed on `uid` rather than cleared outright: the SIGNED_IN listener
      // races this query and may already have resolved the flag for this very
      // account, and nothing here would reschedule that work. Blanking it would
      // strand the demo owner without the tenant entry, and leave a normal
      // owner's readiness false — which suppresses Dashboard's redirect.
      setDemoFlag((prev) => (prev && prev.userId === uid ? prev : null));
      setRoles(rlist);
      const canAccessAdmin = isOwnerOnly(rlist) || hasStaffRole(rlist);
      // If ProtectedRoute bounced them off a deep link on the way in, finish the
      // trip they started — e.g. /dashboard?tab=checkin from a move-out email.
      const returnPath = takeReturnPath({ canAccessAdmin });
      if (returnPath) {
        navigate(returnPath, { replace: true });
      } else if (canAccessAdmin) {
        navigate("/admin?tab=analytics");
      } else if (hasOperatorRole(rlist)) {
        // Operators land in the same console; kept as its own branch so the
        // target can diverge later without touching staff/owner routing.
        navigate("/admin?tab=analytics");
      } else {
        navigate("/dashboard");
      }
    } else {
      const returnPath = takeReturnPath({ canAccessAdmin: false });
      navigate(returnPath ?? "/dashboard", returnPath ? { replace: true } : {});
    }
    return { error: null };
  };

  const signOut = async () => {
    posthog.capture("user signed out");
    // A destination stashed before this session must not outlive it and hijack
    // whoever signs in next in this tab.
    clearReturnPath();
    // Clear UI immediately — auth listener can lag; otherwise nav still shows "Dashboard".
    localStorage.removeItem(IMPERSONATION_TARGET_KEY);
    localStorage.removeItem(IMPERSONATION_ADMIN_SESSION_KEY);
    setImpersonatedUser(null);
    setSession(null);
    setUser(null);
    setRoles([]);
    setDemoFlag(null);
    setErrorTrackingUser(null);
    posthog.reset();
    try {
      await supabase.auth.signOut({ scope: "global" });
    } catch (err) {
      console.error("signOut failed:", err);
    }
    toast.success("Logged out successfully!");
    navigate("/", { replace: true });
  };

  const resetPasswordForEmail = async (email: string) => {
    const redirectUrl = getSiteUrlForPath("/reset-password");
    const language =
      typeof window !== "undefined" && localStorage.getItem("language") === "de"
        ? "de"
        : "en";

    const { data, error } = await supabase.functions.invoke(
      "send-password-reset",
      {
        body: { email, language, redirectTo: redirectUrl },
      }
    );

    if (error || (data && data.success === false)) {
      const message =
        (data && (data.error as string)) ||
        (error && error.message) ||
        "Failed to send reset email";
      toast.error(message);
      return { error: error ?? new Error(message) };
    }

    posthog.capture("password reset requested");
    toast.success("Password reset email sent! Please check your inbox.");
    return { error: null };
  };

  const resetPassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) {
      // These two cases are handled with dedicated copy/redirects by the
      // reset-password page itself — toasting the raw message here would
      // surface Supabase's wording right alongside that friendly handling.
      if (!isTimeoutLikeAuthError(error) && !isSessionMissingAuthError(error)) {
        toast.error(error.message);
      }
      return { error };
    }

    toast.success("Password updated successfully!");
    return { error: null };
  };

  const refreshSession = async () => {
    try {
      const {
        data: { session: refreshedSession },
        error,
      } = await supabase.auth.refreshSession();

      if (error) {
        console.error("Failed to refresh session:", error);

        // Handle specific error cases
        const isExpired =
          error.message.includes("refresh_token_not_found") ||
          error.message.includes("invalid_grant") ||
          error.message.includes("JWT expired") ||
          error.message.includes("Token has expired");

        if (isExpired) {
          // Session is invalid, sign out user — local scope only, so a stale
          // token in THIS tab never revokes the user's other tabs/devices.
          await supabase.auth.signOut({ scope: "local" });
          setRoles([]);
          setSession(null);
          setUser(null);
          toast.error("Your session has expired. Please sign in again.");
          navigate("/");
          return;
        }

        // For other errors, log but don't sign out (might be network issue)
        console.warn(
          "Session refresh failed, but session may still be valid:",
          error.message
        );
        return;
      }

      if (refreshedSession) {
        setSession(refreshedSession);
        setUser(refreshedSession.user);
        if (refreshedSession.user) {
          loadRolesForUser(refreshedSession.user.id);
        }
      }
    } catch (error: any) {
      console.error("Error refreshing session:", error);

      // Handle network errors
      if (
        error.message?.includes("network") ||
        error.message?.includes("fetch")
      ) {
        console.warn(
          "Network error during session refresh, session may still be valid"
        );
        // Don't sign out on network errors, session might still be valid
        return;
      }

      // For other unexpected errors, sign out to be safe — local scope only.
      await supabase.auth.signOut({ scope: "local" });
      setRoles([]);
      setSession(null);
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isAdmin: hasConsoleAccess,
        roles,
        isSuperAdmin,
        canActAs,
        isTech,
        hasSalesAdminRole,
        hasStaffRole: hasStaffRoleFlag,
        hasOwnerRole,
        isOwnerPortalOnly,
        isDemoDualViewAccount,
        isDemoDualViewReady,
        hasOperatorRole: hasOperatorRoleFlag,
        isOperatorPortal: isOperatorPortalFlag,
        hasTechOrSuperAdmin,
        canManageTenant: canManageTenantFlag,
        loading,
        rolesReady,
        signUp,
        signIn,
        signOut,
        resetPasswordForEmail,
        resetPassword,
        refreshSession,
        refreshRoles,
        isImpersonating,
        impersonatedUser,
        actAsTenant,
        actAsOwner,
        stopImpersonating,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
